/**
 * MapLibre's side of the map's style: a flat map, the water recoloured, the basemap labelled in the page's language.
 * The colours and paint themselves are in map/style.ts.
 */
import { waterColor, type MapTheme } from '../map/style';

/**
 * Keep a map flat and north up: pan and zoom only. Pitch is capped at 0 for every input, and
 * rotation is turned off for the mouse (right drag, ctrl drag), two fingers and the keyboard.
 * Touch pitch is turned off too: capped, it would still take two-finger swipes and do nothing.
 */
export function keepMapFlat(map: maplibregl.Map): void {
  map.setMaxPitch(0);
  map.dragRotate.disable();
  map.touchPitch.disable();
  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();
}

/**
 * Apply custom styling to the base map after it loads.
 * Call this in the map's 'load' event handler.
 */
export function applyMapCustomizations(map: maplibregl.Map, theme: MapTheme = 'light'): void {
  if (map.getLayer('water')) {
    map.setPaintProperty('water', 'fill-color', waterColor(theme));
  }
}

/**
 * The basemap label expression for a language: the tile's `name:{lang}` field, the local
 * `name` where the tiles have no translation. Carto's tiles carry `name:xx` for every locale
 * this site can enable.
 */
export function labelTextField(lang: string): unknown[] {
  return ['coalesce', ['get', `name:${lang}`], ['get', 'name']];
}

/**
 * The ids of the layers Carto labels in English (`{name_en}`: places, countries, regions and
 * rivers); roads, parks, seas and house numbers are left alone, labelled in the local name.
 * A layer already relabelled by `setBasemapLanguage` matches too, so the language can change
 * in place. Carto's zoom stops (English zoomed out, local zoomed in) give way to one language
 * at every zoom, which is the point.
 */
export function relabelledLayerIds(layers: maplibregl.LayerSpecification[]): string[] {
  return layers
    .filter((layer) => layer.type === 'symbol' && /name_en|name:/.test(JSON.stringify(layer.layout?.['text-field'] ?? '')))
    .map((layer) => layer.id);
}

/** Label the basemap in the page language. A style still loading has no layers yet and is labelled on load instead. */
export function setBasemapLanguage(map: maplibregl.Map, lang: string): void {
  for (const id of relabelledLayerIds(map.getStyle()?.layers ?? [])) map.setLayoutProperty(id, 'text-field', labelTextField(lang));
}
